/**
 * @module composables/useLatest
 *
 * Composable for managing the "Latest Episodes" view. This view shows
 * the most recently discovered episodes across all monitored podcasts.
 * Supports list and grid view modes, sorting, search/filter, episode
 * removal, and the "Move to Podcast" feature.
 *
 * All refs are module-scoped singletons so state persists when
 * navigating away and back (e.g., to the media player).
 */

import { ref } from "vue";
import { api } from "@/api/config";
import { useSearchEpisodes } from "@/composables/useSearchEpisodes.js";

/** Whether the latest episodes context sub-menu (bottom sheet) is open */
const subMenuOpen = ref(false);

/** Search and download status filter state, shared with episode search composable */
const { download_status, searchQuery, selectedTagId } = useSearchEpisodes();

/** Controls visibility of the "Move to Podcast" dialog (added October 2025) */
const showMoveToPodcast = ref(false);

/**
 * Flag to skip fetching latest episodes on next mount.
 * Used when returning from media player to avoid redundant API calls.
 */
const doNoGetLatest = ref(false);

/** Snackbar notification state for latest-view-specific messages */
const snackbar = ref({
  show: false,
  message: null,
  color: "info",
});

/** Array of latest episode objects currently loaded for display */
const latest = ref([]);

/** Total count of episodes in the latest list (from first episode's total_episodes) */
const total_latest = ref(0);

/** Whether multi-select mode is active for bulk operations */
const selectItems = ref(false);

/** Array of selected episode IDs when in multi-select mode */
const selected = ref([]);

/** Controls the episode info overlay/panel visibility (persists across components) */
const showInfo = ref(false);

/** State for the remove episode confirmation modal */
const removeEpisodeModal = ref({
  show: false,
});

/** Controls the latest list-level bottom sheet menu visibility */
const sheetLatestMenu = ref(false);

/** Controls the individual episode bottom sheet menu visibility */
const sheetLatestEpisodeMenu = ref(false);

/** The currently selected episode for context menu actions */
const currentLatestEpisode = ref();

/** Whether a latest episodes fetch is currently in progress */
const loading = ref(false);

/** Legacy menu visibility refs (may be used by older component code) */
const latestMenu = ref(false);
const latestEpisodeMenu = ref(false);

/**
 * View mode for the latest episodes: "list" for virtual-scrolled list,
 * "grid" for YouTube-style card layout. Persisted in localStorage.
 */
const viewMode = ref(localStorage.getItem("latestViewMode") || "list");

/** Controls visibility of the sort options panel */
const showSort = ref(false);

/** Current sort field for latest episodes (default: updated_at) */
const sort_by = ref("updated_at");

/** Current sort direction (default: newest first) */
const sort_direction = ref("DESC");

/**
 * Per-podcast sort settings restored from localStorage.
 * These arrays are indexed by podcast ID and allow different sort
 * preferences for different podcast contexts.
 */
const sortEpisodesParams = JSON.parse(
  localStorage.getItem("latestEpisodesSort"),
);
const sort_byArray = ref(sortEpisodesParams?.sort_byArray || []);
const sort_directionArray = ref(sortEpisodesParams?.sort_directionArray || []);

/**
 * Toggle the sort options panel visibility and close the main menu.
 */
const handleSort = async () => {
  showSort.value = !showSort.value;
  sheetLatestMenu.value = false;
};

/**
 * Clears the user's entire latest list (server + local state).
 * Mirrors usePlaylist's clearAll.
 */
const clearAll = async () => {
  await api.delete("/latest/clear");
  latest.value = [];
  total_latest.value = 0;
};

/**
 * Fetch the latest episodes from the server.
 * Respects the doNoGetLatest flag to skip redundant fetches (e.g.,
 * when returning from media player). Passes current search query,
 * download status filter, and sort parameters as query params.
 *
 * The total count is extracted from the first episode's total_episodes
 * field, which the server includes as a convenience.
 */
const getLatest = async () => {
  if (doNoGetLatest.value) {
    // Skip this fetch and reset the flag for next time
    doNoGetLatest.value = false;
  } else {
    loading.value = true;
    const params = {
      d: download_status.value,
      q: searchQuery.value,
      tag: selectedTagId.value, // tag filter from the search dialog
      sort_by: sort_by.value,
      sort_direction: sort_direction.value,
    };
    try {
      const { data } = await api.get("/latest", {
        params,
      });
      // Check for application-level error in the response body
      if (!data.success) {
        throw new Error(data.error.message);
      }
      latest.value = data.latest;
      // Total count comes from the first episode's metadata (server-side convenience)
      total_latest.value = data.latest[0]?.total_episodes || 0;
      loading.value = false;
    } catch (error) {
      loading.value = false;
      console.error(error);
    }
  }
};

/**
 * Remove an episode from the latest list by its ID.
 * Closes the confirmation modal, calls the server DELETE endpoint,
 * then removes the episode from the local array for immediate UI update.
 * Updates the total count from the server response.
 *
 * @param {number} id - The episode ID to remove from latest
 */
const removeEpisode = async (id) => {
  removeEpisodeModal.value.show = false;
  try {
    const { data } = await api.delete(`/latest/${id}`);
    // Remove the episode from the local array to update UI immediately.
    // Capture its title first so we can show feedback after it's gone.
    const existingIndex = latest.value.findIndex(
      (episode) => episode.id === id,
    );
    const removedTitle =
      existingIndex >= 0 ? latest.value[existingIndex].title : null;
    if (existingIndex >= 0) {
      latest.value.splice(existingIndex, 1);
    }
    sheetLatestEpisodeMenu.value = false; // Close the episode bottom sheet menu
    // Update total count from server response
    total_latest.value = data.total_latest;
    // Show success feedback so the swipe-to-remove isn't silent — in the virtual
    // scroll the list just shifts up by one, which otherwise looks like nothing
    // happened (the swipe-to-add action shows a snackbar, so this matches it).
    snackbar.value = {
      show: true,
      color: "success",
      message: removedTitle
        ? `"${removedTitle}" removed from Latest`
        : "Removed from Latest",
    };
  } catch (error) {
    snackbar.value.show = true;
    snackbar.value.color = "error";
    snackbar.value.message = `Error removing from latest: ${error}`;
    console.error("Error removing from latest:", error);
  } finally {
  }
};

/**
 * Composable function returning all latest episodes state and functions.
 *
 * @returns {Object} Latest episodes state refs and action functions
 */
export function useLatest() {
  return {
    // dicembre 13, 2025
    subMenuOpen,
    //
    loading,
    clearAll,
    handleSort,
    getLatest,
    removeEpisode,
    // play,
    //
    removeEpisodeModal,
    latest,
    total_latest,
    selectItems,
    selected,
    snackbar,
    latestMenu,
    latestEpisodeMenu,
    currentLatestEpisode,
    showInfo,
    sheetLatestMenu,
    sheetLatestEpisodeMenu,
    doNoGetLatest,
    // sort
    showSort,
    sort_by,
    sort_byArray,
    sort_direction,
    sort_directionArray,
    //
    showMoveToPodcast,
    // view mode
    viewMode,
  };
}
